import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { publicTemplates } from '../../services/adminService';
import { TEMPLATE_CONFIGS } from '../../types/cv';
import { initialData } from '../../hooks/useCVBuilder';
import { clearDraft } from '../../utils/cvDraft';
import { trackEvent } from '../../utils/analytics';
import CVPreview from '../cv-builder/CVPreview';

type GalleryTemplate = {
    id: string;
    name: string;
    hex?: string;
};

const A4_WIDTH = 794;
const A4_HEIGHT = 1123;
const PREVIEW_SCALE = 0.28;

const TemplateGallery: React.FC = () => {
    const navigate = useNavigate();
    const { logout, user } = useAuth();
    const [templates, setTemplates] = useState<GalleryTemplate[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        publicTemplates('cv')
            .then((list: any[]) => {
                const opts = (list || []).map((t) => ({
                    id: t.slug || t.id,
                    name: t.name,
                    hex: t.hex,
                }));
                setTemplates(
                    opts.length
                        ? opts
                        : TEMPLATE_CONFIGS.map((t) => ({ id: t.id, name: t.name, hex: t.hex }))
                );
            })
            .catch(() => {
                setTemplates(TEMPLATE_CONFIGS.map((t) => ({ id: t.id, name: t.name, hex: t.hex })));
            })
            .finally(() => setLoading(false));
    }, []);

    const handleSelect = (slug: string, name?: string) => {
        trackEvent('template_selected', { template_id: slug, template_name: name || '' });
        clearDraft(user?._id || null, null);
        navigate(`/builder?template=${encodeURIComponent(slug)}`);
    };

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => navigate('/')}
                            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors"
                            aria-label="Retour à l'accueil"
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <div>
                            <h1 className="text-lg font-bold text-slate-900">Choisir un modèle</h1>
                            <p className="text-sm text-slate-500 hidden sm:block">
                                Sélectionnez un modèle pour commencer à modifier votre CV
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={logout}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <LogOut size={16} />
                        <span className="hidden sm:inline">Déconnexion</span>
                    </button>
                </div>
            </header>

            <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-8">
                {loading ? (
                    <div className="flex items-center justify-center py-24">
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600" />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                        {templates.map((tpl) => (
                            <button
                                key={tpl.id}
                                type="button"
                                onClick={() => handleSelect(tpl.id, tpl.name)}
                                className="group text-left bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                            >
                                <div
                                    className="relative bg-slate-100 overflow-hidden flex items-start justify-center pt-4"
                                    style={{ height: A4_HEIGHT * PREVIEW_SCALE + 24 }}
                                >
                                    <div
                                        className="origin-top shadow-md bg-white pointer-events-none"
                                        style={{
                                            width: A4_WIDTH,
                                            height: A4_HEIGHT,
                                            transform: `scale(${PREVIEW_SCALE})`,
                                        }}
                                    >
                                        <CVPreview data={initialData} template={tpl.id} />
                                    </div>
                                </div>
                                <div className="p-4 flex items-center justify-between gap-3 border-t border-slate-100">
                                    <div className="min-w-0">
                                        <p className="font-semibold text-slate-900 truncate">{tpl.name}</p>
                                        <p className="text-xs text-slate-500 mt-0.5 group-hover:text-indigo-600 transition-colors">
                                            Utiliser ce modèle
                                        </p>
                                    </div>
                                    {tpl.hex && (
                                        <span
                                            className="w-4 h-4 rounded-full shrink-0 ring-1 ring-black/10"
                                            style={{ backgroundColor: tpl.hex }}
                                            aria-hidden
                                        />
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

export default TemplateGallery;
